import { baseApi } from "../../../../app/api";
import { store } from "../../../../app/store";
import { selectLanguageInfo } from "../../../../common/features/language/languageSelectors";
import { transformProduct } from "../../product/helpers/productApiHelpers/transformProduct";

import type { RawProduct } from "../../../../types";
import type { TransformedProduct } from "../../product/types/productApiTypes";

const CUSTOM_REST_API_BASE =
  window.commonVariables?.customRestApiBase || "/wp-json/ecommerce-app/v1";

export const savedForLaterApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    /**
     * Retrieves the list of saved IDs from Session/User Meta.
     */
    getSavedForLaterItemIds: builder.query<number[], void>({
      query: () => ({
        url: `${CUSTOM_REST_API_BASE}/saved-for-later`,
        method: "GET",
      }),
      providesTags: ["SavedForLater"],
    }),

    /**
     * Fetches detailed product data for a specific list of IDs.
     */
    getSavedForLaterItemsData: builder.query<TransformedProduct[], number[]>({
      query: (ids) => ({
        url: `${CUSTOM_REST_API_BASE}/products/batch`,
        params: { include: ids.join(",") },
      }),
      transformResponse: (response: RawProduct[]) => {
        if (!Array.isArray(response)) return [];
        const languageInfo = selectLanguageInfo(store.getState());
        return response
          .map((product) => transformProduct(product, languageInfo))
          .filter((item): item is TransformedProduct => item !== null);
      },
    }),

    /**
     * Adds an ID.
     */
    addSavedForLaterItem: builder.mutation<number[], number>({
      query: (id) => ({
        url: `${CUSTOM_REST_API_BASE}/saved-for-later/add`,
        method: "POST",
        body: { id },
      }),
      invalidatesTags: ["SavedForLater"],
    }),

    /**
     * Removes an ID.
     */
    removeSavedForLaterItem: builder.mutation<number[], number>({
      query: (id) => ({
        url: `${CUSTOM_REST_API_BASE}/saved-for-later/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["SavedForLater"],
    }),
  }),
});

export const {
  useGetSavedForLaterItemIdsQuery,
  useGetSavedForLaterItemsDataQuery,
  useAddSavedForLaterItemMutation,
  useRemoveSavedForLaterItemMutation,
} = savedForLaterApi;
